import React, { useState } from 'react';
import { X, Mail, Lock, Eye, EyeOff, User, Phone, ArrowLeft, Loader2, KeyRound, Truck, ShoppingBag, Users, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { authAPI, passwordResetAPI, languageAPI } from '../services/api';
import { toast } from 'sonner';
import { useLanguage, LANGUAGES } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';
import CustomerGuide from './CustomerGuide';

// Role options for sign up
const ROLE_OPTIONS = [
  { id: 'customer', label: 'Customer', icon: ShoppingBag, description: 'Shop and order groceries' },
  { id: 'staff', label: 'Staff', icon: Users, description: 'Work at the store' },
  { id: 'driver', label: 'Driver', icon: Truck, description: 'Deliver orders' },
];

// Google Icon SVG
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" className="mr-2">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const SignInModal = ({ isOpen, onClose, onSignIn }) => {
  const { t, language, setLanguage, syncWithUser } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('signin');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: enter email, 2: enter code & new password
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);
  const [showCustomerGuide, setShowCustomerGuide] = useState(false);
  
  // Sign In form state
  const [signInData, setSignInData] = useState({
    email: '',
    password: '',
  });

  // Sign Up form state
  const [signUpData, setSignUpData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'customer', // Default role
    // Driver-specific fields
    vehicleType: '',
    licenseNumber: '',
    // Staff-specific fields
    employeeId: '',
  });

  const [errors, setErrors] = useState({});

  // Handle password reset request
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      setErrors({ reset: 'Please enter your email address' });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await passwordResetAPI.requestReset(resetEmail);
      setResetSent(true);
      setResetStep(2);
      toast.success('Password reset code sent to your email');
    } catch (error) {
      setErrors({ reset: error.message || 'Failed to send reset email. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset confirmation
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetToken) {
      setErrors({ reset: 'Please enter the reset code from your email' });
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setErrors({ reset: 'Password must be at least 6 characters' });
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setErrors({ reset: 'Passwords do not match' });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await passwordResetAPI.confirmReset(resetToken, newPassword);
      toast.success('Password successfully reset! You can now log in.');
      // Reset all states
      setShowForgotPassword(false);
      setResetSent(false);
      setResetStep(1);
      setResetEmail('');
      setResetToken('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      setErrors({ reset: error.message || 'Invalid or expired reset code. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    // Validate
    if (!signInData.email) {
      setErrors({ email: 'Email is required' });
      setIsLoading(false);
      return;
    }
    if (!signInData.password) {
      setErrors({ password: 'Password is required' });
      setIsLoading(false);
      return;
    }

    try {
      // Call real API
      const response = await authAPI.login(signInData.email, signInData.password);
      
      // Create user object from response
      const user = {
        id: response.user.id,
        name: response.user.name,
        email: response.user.email,
        phone: response.user.phone,
        role: response.user.role,
        isAdmin: response.user.is_admin,
        orderCount: response.user.order_count,
      };

      onSignIn(user);
      toast.success(`Welcome back, ${user.name}!`);
      onClose();
    } catch (error) {
      setErrors({ general: error.message || 'Invalid email or password' });
      toast.error(error.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    // Validate common fields
    if (!signUpData.name) {
      setErrors({ name: 'Name is required' });
      setIsLoading(false);
      return;
    }
    if (!signUpData.email) {
      setErrors({ email: 'Email is required' });
      setIsLoading(false);
      return;
    }
    if (!signUpData.password) {
      setErrors({ password: 'Password is required' });
      setIsLoading(false);
      return;
    }
    if (signUpData.password !== signUpData.confirmPassword) {
      setErrors({ confirmPassword: 'Passwords do not match' });
      setIsLoading(false);
      return;
    }
    if (signUpData.password.length < 6) {
      setErrors({ password: 'Password must be at least 6 characters' });
      setIsLoading(false);
      return;
    }

    // Role-specific validation
    if (signUpData.role === 'driver') {
      if (!signUpData.phone) {
        setErrors({ phone: 'Phone number is required for drivers' });
        setIsLoading(false);
        return;
      }
      if (!signUpData.vehicleType) {
        setErrors({ vehicleType: 'Vehicle type is required for drivers' });
        setIsLoading(false);
        return;
      }
    }

    if (signUpData.role === 'staff') {
      if (!signUpData.phone) {
        setErrors({ phone: 'Phone number is required for staff' });
        setIsLoading(false);
        return;
      }
    }

    try {
      // Call real API with role
      const response = await authAPI.register(
        signUpData.name,
        signUpData.email,
        signUpData.password,
        signUpData.phone || null,
        signUpData.role,
        signUpData.role === 'driver' ? {
          vehicle_type: signUpData.vehicleType,
          license_number: signUpData.licenseNumber,
        } : signUpData.role === 'staff' ? {
          employee_id: signUpData.employeeId,
        } : null
      );
      
      // Create user object from response
      const user = {
        id: response.user.id,
        name: response.user.name,
        email: response.user.email,
        phone: response.user.phone,
        role: response.user.role,
        isAdmin: response.user.is_admin,
        orderCount: response.user.order_count,
      };

      onSignIn(user);
      toast.success(`Welcome, ${user.name}! Account created successfully.`);
      onClose();
    } catch (error) {
      setErrors({ general: error.message || 'Registration failed' });
      toast.error(error.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Google Sign In Handler
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    setIsGoogleLoading(true);
    // Use window.location.origin to get the current domain dynamically
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (!isOpen) return null;

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />
        
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden z-10">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-10"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>

          <div className="bg-gradient-to-r from-blue-900 to-blue-950 px-6 py-8 text-white text-center">
            <h2 className="text-2xl font-bold">Reset Password</h2>
            <p className="text-blue-300 mt-1">We&apos;ll send you a reset link</p>
          </div>

          <div className="p-6">
            <button
              onClick={() => {
                setShowForgotPassword(false);
                setResetSent(false);
                setErrors({});
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </button>

            {resetStep === 2 ? (
              /* Step 2: Enter reset code and new password */
              <form onSubmit={handleResetPassword} className="space-y-4">
                {errors.reset && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {errors.reset}
                  </div>
                )}

                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-blue-300 rounded-full flex items-center justify-center mx-auto mb-3">
                    <KeyRound className="w-6 h-6 text-blue-900" />
                  </div>
                  <p className="text-gray-600 text-sm">
                    Enter the reset code sent to <strong>{resetEmail}</strong>
                  </p>
                </div>

                <div>
                  <Label htmlFor="reset-token">Reset Code</Label>
                  <Input
                    id="reset-token"
                    type="text"
                    placeholder="Paste your reset code"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    className="mt-1 font-mono text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="confirm-new-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-900 hover:bg-blue-950 text-white py-6"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  Did not receive the code?{' '}
                  <button 
                    type="button"
                    onClick={() => {
                      setResetStep(1);
                      setResetSent(false);
                    }} 
                    className="text-blue-900 hover:underline"
                  >
                    Try again
                  </button>
                </p>
              </form>
            ) : (
              /* Step 1: Enter email */
              <form onSubmit={handleForgotPassword} className="space-y-4">
                {errors.reset && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {errors.reset}
                  </div>
                )}

                <div>
                  <Label htmlFor="reset-email">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="Enter your email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-blue-900 hover:bg-blue-950 text-white py-6"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('loading')}
                    </>
                  ) : (
                    t('sendResetCode')
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden z-10">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors z-10"
        >
          <X className="w-4 h-4 text-gray-600" />
        </button>

        {/* Language Selector */}
        <div className="absolute top-4 left-4 z-10">
          <LanguageSelector variant="dropdown" showLabel={false} />
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-950 px-6 py-8 text-white text-center">
          <h2 className="text-2xl font-bold">UPLANDS FOOD MARKET</h2>
          <p className="text-blue-300 mt-1">Your Neighborhood African & Caribbean Grocery Store</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="p-6">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="signin">{t('signIn')}</TabsTrigger>
            <TabsTrigger value="signup">{t('signUp')}</TabsTrigger>
          </TabsList>

          {/* Sign In Tab */}
          <TabsContent value="signin">
            <form onSubmit={handleSignIn} className="space-y-4">
              {errors.general && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {errors.general}
                </div>
              )}
              <div>
                <Label htmlFor="signin-email">{t('email')}</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder="Enter your email"
                    value={signInData.email}
                    onChange={(e) => setSignInData({ ...signInData, email: e.target.value })}
                    className="pl-10"
                  />
                </div>
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="signin-password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="signin-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={signInData.password}
                    onChange={(e) => setSignInData({ ...signInData, password: e.target.value })}
                    className="pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" className="rounded border-gray-300" />
                  <span className="text-gray-600">Remember me</span>
                </label>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowForgotPassword(true);
                    setResetEmail(signInData.email);
                    setResetSent(false);
                    setErrors({});
                  }}
                  className="text-blue-900 hover:text-blue-950"
                >
                  Forgot password?
                </button>
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">or continue with</span>
                </div>
              </div>

              {/* Google Sign In Button */}
              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading}
                className="w-full py-6 border-gray-300 hover:bg-gray-50 flex items-center justify-center"
                data-testid="google-signin-btn"
              >
                {isGoogleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <GoogleIcon />
                )}
                {isGoogleLoading ? 'Redirecting...' : 'Continue with Google'}
              </Button>
            </form>
          </TabsContent>

          {/* Sign Up Tab */}
          <TabsContent value="signup">
            <form onSubmit={handleSignUp} className="space-y-4">
              {errors.general && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {errors.general}
                </div>
              )}

              {/* Role Selection */}
              <div>
                <Label className="mb-2 block">I am a...</Label>
                <div className="grid grid-cols-3 gap-2">
                  {ROLE_OPTIONS.map((role) => {
                    const Icon = role.icon;
                    return (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => setSignUpData({ ...signUpData, role: role.id })}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          signUpData.role === role.id
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                        data-testid={`role-${role.id}`}
                      >
                        <Icon className="w-5 h-5 mx-auto mb-1" />
                        <span className="text-xs font-medium">{role.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="signup-name">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="signup-name"
                    type="text"
                    placeholder="Enter your full name"
                    value={signUpData.name}
                    onChange={(e) => setSignUpData({ ...signUpData, name: e.target.value })}
                    className="pl-10"
                  />
                </div>
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <Label htmlFor="signup-email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="Enter your email"
                    value={signUpData.email}
                    onChange={(e) => setSignUpData({ ...signUpData, email: e.target.value })}
                    className="pl-10"
                  />
                </div>
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              {/* Phone - Required for Staff and Driver, Optional for Customer */}
              <div>
                <Label htmlFor="signup-phone">
                  Phone Number {signUpData.role !== 'customer' && <span className="text-red-500">*</span>}
                </Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="signup-phone"
                    type="tel"
                    placeholder="+1 (613) 555-0123"
                    value={signUpData.phone}
                    onChange={(e) => setSignUpData({ ...signUpData, phone: e.target.value })}
                    className="pl-10"
                  />
                </div>
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              {/* Driver-specific fields */}
              {signUpData.role === 'driver' && (
                <>
                  <div>
                    <Label htmlFor="signup-vehicle">Vehicle Type <span className="text-red-500">*</span></Label>
                    <div className="relative mt-1">
                      <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <select
                        id="signup-vehicle"
                        value={signUpData.vehicleType}
                        onChange={(e) => setSignUpData({ ...signUpData, vehicleType: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select vehicle type</option>
                        <option value="car">Car</option>
                        <option value="suv">SUV</option>
                        <option value="van">Van</option>
                        <option value="truck">Truck</option>
                        <option value="motorcycle">Motorcycle</option>
                        <option value="bicycle">Bicycle</option>
                      </select>
                    </div>
                    {errors.vehicleType && <p className="text-red-500 text-sm mt-1">{errors.vehicleType}</p>}
                  </div>
                  <div>
                    <Label htmlFor="signup-license">License Number (Optional)</Label>
                    <Input
                      id="signup-license"
                      type="text"
                      placeholder="Driver's license number"
                      value={signUpData.licenseNumber}
                      onChange={(e) => setSignUpData({ ...signUpData, licenseNumber: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </>
              )}

              {/* Staff-specific fields */}
              {signUpData.role === 'staff' && (
                <div>
                  <Label htmlFor="signup-employee-id">Employee ID (Optional)</Label>
                  <Input
                    id="signup-employee-id"
                    type="text"
                    placeholder="If provided by employer"
                    value={signUpData.employeeId}
                    onChange={(e) => setSignUpData({ ...signUpData, employeeId: e.target.value })}
                    className="mt-1"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="signup-password">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a password"
                    value={signUpData.password}
                    onChange={(e) => setSignUpData({ ...signUpData, password: e.target.value })}
                    className="pl-10 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>

              <div>
                <Label htmlFor="signup-confirm">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="signup-confirm"
                    type="password"
                    placeholder="Confirm your password"
                    value={signUpData.confirmPassword}
                    onChange={(e) => setSignUpData({ ...signUpData, confirmPassword: e.target.value })}
                    className="pl-10"
                  />
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6"
              >
                {isLoading ? 'Creating account...' : `Create ${signUpData.role.charAt(0).toUpperCase() + signUpData.role.slice(1)} Account`}
              </Button>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">or sign up with</span>
                </div>
              </div>

              {/* Google Sign Up Button */}
              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleSignIn}
                disabled={isGoogleLoading}
                className="w-full py-6 border-gray-300 hover:bg-gray-50 flex items-center justify-center"
                data-testid="google-signup-btn"
              >
                {isGoogleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <GoogleIcon />
                )}
                {isGoogleLoading ? 'Redirecting...' : 'Sign up with Google'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
        
        {/* Footer Credits */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 leading-relaxed">
              DESIGNED By <span className="font-semibold">ictglobalinitiatives</span> & <span className="font-semibold">WHICHBusiness.CANADA</span>
            </p>
            <button
              onClick={() => setShowCustomerGuide(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-colors"
              data-testid="customer-guide-btn"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="font-bold text-sm">HELP</span>
            </button>
          </div>
        </div>

        {/* Customer Guide Modal */}
        <CustomerGuide 
          isOpen={showCustomerGuide} 
          onClose={() => setShowCustomerGuide(false)} 
        />
      </div>
    </div>
  );
};

export default SignInModal;
